import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { CircleMinus, Gift } from "lucide-react";

import { UIButton, UICard, UIIcon } from "../../../../../ui";
import { clx } from "../../../../../../utils/Helpers";
import { formatPayrollAmount } from "../utils/formatPayrollValue";
import EventPayrollSectionEmptyState from "./EventPayrollSectionEmptyState";

const EventPayrollAdjustmentSection = ({
  variant,
  title,
  meta,
  total,
  entries,
  labels,
  onAdd,
  onRevertEntry,
  canRevert,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  className,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const hasEntries = entries.length > 0;
  const cardType = variant === "deduction" ? "destructive" : "success";
  const addButtonVariant = variant === "deduction" ? "destructive" : "success";
  const revertIconName = lang === "ar" ? "undo2" : "redo2";
  const totalClassName =
    variant === "deduction"
      ? "event-payroll-generate__card-total event-payroll-generate__card-total--destructive text-label-lg"
      : "event-payroll-generate__card-total event-payroll-generate__card-total--success text-label-lg";
  const cardClassName = clx(
    className,
    "event-payroll-generate__adjustment-card",
    "event-payroll-generate__list-card",
  );

  return (
    <UICard
      type={cardType}
      className={cardClassName}
      title={title}
      subtitle={meta || undefined}
      action={
        <div className="event-payroll-generate__adjustment-actions">
          <span className={totalClassName}>
            {formatPayrollAmount(total, { signed: true })}
          </span>
          {onAdd ? (
            <UIButton
              type="button"
              variant={addButtonVariant}
              appearance="outline"
              size="small"
              className="event-payroll-generate__add-btn"
              onClick={onAdd}
              aria-label={labels.add}
            >
              <UIIcon name="plus" size={16} decorative />
            </UIButton>
          ) : null}
        </div>
      }
    >
      {hasEntries ? (
        <ul className="event-payroll-generate__adjustment-list">
          {entries.map((entry) => (
            <li key={entry.id} className="event-payroll-generate__adjustment-item">
              <div className="event-payroll-generate__adjustment-copy">
                <span className="event-payroll-generate__adjustment-type text-label-sm">
                  {entry.type === "auto" ? labels.typeAuto : labels.typeManual}
                </span>
                <span className="event-payroll-generate__adjustment-reason text-label-sm">
                  {entry.reason}
                </span>
              </div>
              <div className="event-payroll-generate__adjustment-side">
                <span
                  className={clx(
                    variant === "deduction"
                      ? "event-payroll-generate__adjustment-amount text-label-md"
                      : "event-payroll-generate__adjustment-amount text-label-sm",
                    variant === "deduction"
                      ? "event-payroll-generate__adjustment-amount--negative"
                      : "event-payroll-generate__adjustment-amount--positive",
                  )}
                >
                  {formatPayrollAmount(entry.amount, { signed: true })}
                </span>
                {entry.type === "auto" ? (
                  <span className="event-payroll-generate__system-tag text-label-xs">
                    {labels.systemTag}
                  </span>
                ) : null}
                {entry.type === "manual" &&
                entry.canRevert &&
                canRevert &&
                onRevertEntry ? (
                  <UIButton
                    type="button"
                    variant={variant === "deduction" ? "destructive" : "success"}
                    appearance="outline"
                    size="small"
                    className="event-payroll-generate__entry-revert"
                    onClick={() => onRevertEntry(entry)}
                    aria-label={labels.revertEntry}
                  >
                    <UIIcon name={revertIconName} size={16} strokeWidth={2} decorative />
                  </UIButton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EventPayrollSectionEmptyState
          tone={addButtonVariant}
          icon={
            variant === "deduction" ? (
              <CircleMinus size={24} />
            ) : (
              <Gift size={24} />
            )
          }
          title={emptyDescription}
          actionLabel={emptyActionLabel}
          actionIcon={<UIIcon name="plus" size={16} decorative />}
          onAction={onEmptyAction}
          actionVariant={addButtonVariant}
        />
      )}
    </UICard>
  );
};

EventPayrollAdjustmentSection.propTypes = {
  variant: PropTypes.oneOf(["deduction", "reward"]).isRequired,
  title: PropTypes.string.isRequired,
  meta: PropTypes.string,
  total: PropTypes.number,
  entries: PropTypes.arrayOf(PropTypes.object),
  labels: PropTypes.shape({
    add: PropTypes.string.isRequired,
    typeAuto: PropTypes.string.isRequired,
    typeManual: PropTypes.string.isRequired,
    systemTag: PropTypes.string.isRequired,
    revertEntry: PropTypes.string.isRequired,
  }).isRequired,
  onAdd: PropTypes.func,
  onRevertEntry: PropTypes.func,
  canRevert: PropTypes.bool,
  emptyDescription: PropTypes.string,
  emptyActionLabel: PropTypes.string,
  onEmptyAction: PropTypes.func,
  className: PropTypes.string,
};

EventPayrollAdjustmentSection.defaultProps = {
  meta: "",
  total: 0,
  entries: [],
  onAdd: undefined,
  onRevertEntry: undefined,
  canRevert: true,
  emptyDescription: "",
  emptyActionLabel: "",
  onEmptyAction: undefined,
  className: "ui-card--section",
};

export default EventPayrollAdjustmentSection;
